import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { api } from "../lib/api";
import { SearchInput } from "./SearchInput";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { PresenceGate } from "../lib/presence-gate";

type SearchResult = { id: string; title: string; type: string };

const MIN_QUERY_LENGTH = 3;
const MAX_SUGGESTIONS = 6;

function destinationFor(type: string, query: string): string | null {
  const params = `q=${encodeURIComponent(query)}`;
  switch (type) {
    case "person": return `/marketplace?tab=Jobseekers&${params}`;
    case "service": return `/marketplace?tab=Services&${params}`;
    case "company": return `/marketplace?tab=Companies&${params}`;
    case "job": return `/marketplace?tab=Jobs&${params}`;
    case "project": return `/marketplace?tab=Project%20Bidding&${params}`;
    case "rfq": return `/marketplace?tab=RFQs&${params}`;
    case "item": return `/marketplace?tab=Marketplace&${params}`;
    default: return null;
  }
}

type HeaderAutocompleteProps = {
  id: string;
  className?: string;
  inputClassName?: string;
  onNavigate?: () => void;
};

/** Shared header typeahead. It reuses unified search, then opens its existing filtered directory. */
export function HeaderAutocomplete({ id, className = "", inputClassName = "field h-9 text-sm", onNavigate }: HeaderAutocompleteProps) {
  const [, navigate] = useLocation();
  const reducedMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trimmed = query.trim();
  const canSearch = trimmed.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    if (!canSearch) {
      setResults([]);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api.search(trimmed)
        .then((data) => {
          if (!alive) return;
          setResults((data?.results || []).filter((result: SearchResult) => result.title && destinationFor(result.type, trimmed)).slice(0, MAX_SUGGESTIONS));
          setActiveIndex(-1);
        })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [canSearch, trimmed]);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  function select(result: SearchResult) {
    const destination = destinationFor(result.type, trimmed);
    if (!destination) return;
    setOpen(false);
    onNavigate?.();
    navigate(destination);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") { setOpen(false); return; }
    if (!canSearch || results.length === 0) return;
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActiveIndex((index) => Math.min(index + 1, results.length - 1)); }
    if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(index - 1, 0)); }
    if (event.key === "Enter" && activeIndex >= 0) { event.preventDefault(); select(results[activeIndex]); }
  }

  return (
    <div className={`relative ${className}`} onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}>
      <SearchInput
        id={id}
        value={query}
        onChange={(value) => { setQuery(value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        role="combobox"
        aria-label="Search marketplace"
        aria-autocomplete="list"
        aria-expanded={open && canSearch}
        aria-controls={open && canSearch ? `${id}-results` : undefined}
        aria-activedescendant={open && canSearch && activeIndex >= 0 ? `${id}-result-${activeIndex}` : undefined}
        placeholder="Search marketplace"
        className={inputClassName}
        iconClassName="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-mut"
      />
      <AnimatePresence>{open && canSearch && (
        <PresenceGate>{({ isPresent, gate }) => <motion.div {...(!isPresent ? { inert: "" } : {})} id={`${id}-results`} initial={reducedMotion ? false : { opacity: 0, clipPath: "inset(0 0 100% 0 round 12px)" }} animate={{ opacity: 1, clipPath: "inset(0 0 0% 0 round 12px)" }} exit={{ opacity: 0, clipPath: "inset(0 0 100% 0 round 12px)" }} transition={{ duration: reducedMotion ? 0 : 0.18, ease: [0.2, 0, 0.2, 1] }} aria-hidden={!isPresent} style={gate.style} className="absolute top-[calc(100%+0.375rem)] left-0 z-50 w-full overflow-hidden rounded-xl border border-line bg-white shadow-lift" role="listbox" aria-label="Search suggestions">
          {loading ? <p className="px-3 py-3 text-sm text-mut">Searching…</p> : results.length === 0 ? <p className="px-3 py-3 text-sm text-mut">No matches found.</p> : results.map((result, index) => (
            <button
              key={`${result.type}:${result.id}`}
              id={`${id}-result-${index}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(result)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`block w-full border-b border-line px-3 py-3 text-left text-sm font-semibold text-navy last:border-b-0 ${index === activeIndex ? "bg-navy-50" : "hover:bg-navy-50"}`}
            >
              {result.title}
            </button>
          ))}
        </motion.div>}</PresenceGate>
      )}</AnimatePresence>
    </div>
  );
}
